import { useCallback } from 'react';
import { getCountryCode, getLanguageCode } from 'typescript/utilities/locales';
import {
  PaymentSchedule,
  PaymentType,
} from 'typescript/constants/enums/payment';
import { TOrderSummary } from 'typescript/types/api/solidshop';
import { isOrderFree, orderIsWarranty } from 'typescript/utilities/order_data';

type CheckoutVersion = 'v2' | 'v3';
type ErrorStage =
  | 'tokenize'
  | 'pre_auth_fraud'
  | 'create_payment'
  | 'complete_order';

interface UseLogCompleteOrderActionParams {
  checkoutVersion?: CheckoutVersion;
  orderNumber: string;
  orderSummaryData?: TOrderSummary;
}

interface LogCompleteOrderActionParams {
  status: 'initiated' | 'success' | 'error' | 'cancelled';
  paymentType?: PaymentType;
  paymentSchedule?: PaymentSchedule;
  errorStage?: ErrorStage;
  errorDetails?: string;
}

/**
 * Hook for logging complete order CTA actions to Datadog RUM.
 * Used across paid (payment page) and free (delivery page) order flows
 * to track CTA presses, successes, and errors under a single action name.
 *
 * @param checkoutVersion - 'v2' or 'v3' to identify which checkout version triggered the action
 * @param orderNumber - the order number to include in the logged action
 * @param orderSummaryData - order summary used to derive orderType, isFreeOrder, and deliveryMethod
 * @returns logCompleteOrderAction - async function to log the action
 */
const useLogCompleteOrderAction = ({
  checkoutVersion = 'v3',
  orderNumber,
  orderSummaryData,
}: UseLogCompleteOrderActionParams) => {
  const logCompleteOrderAction = useCallback(
    async (params: LogCompleteOrderActionParams) => {
      const { datadogRum } = await import('@datadog/browser-rum');

      datadogRum.addAction('complete-order-cta-action', {
        ...params,
        checkoutVersion,
        orderType: orderIsWarranty(orderSummaryData) ? 'warranty' : 'normal',
        isFreeOrder: isOrderFree(orderSummaryData?.payment_due),
        deliveryMethod: orderSummaryData?.bopis_fulfillment_selected
          ? 'pickup_in_store'
          : 'ship_to_me',
        orderNumber,
        countryCode: getCountryCode(),
        languageCode: getLanguageCode(),
      });
    },
    [checkoutVersion, orderSummaryData, orderNumber],
  );

  return { logCompleteOrderAction };
};

export { useLogCompleteOrderAction };
